import React, { FC, useRef, useState } from 'react';
import {
    ExtendedAdaptedCustomLine,
    ExtendedAdaptedRecommendedLine,
    ExtendedRenewalIndividual,
    RenewalLineView
} from '../../utils/renewalTableTileTypes';
import { RenewalLineCell } from '../simple/RenewalLineCell';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { RenewalLineItems } from '../RenewalLineItems';
import { IssueUsageStatisticsCell } from '../../../../shared/subscription-management/IssueUsageStatisticsCell';
import { ExtendedManageLineUsageLimit } from '../../../../shared/subscription-management/types/ExtendedManageLineShared';
import { NoTotalCell, RenewalLineTotal } from '../RenewalLineTotal';
import { StockDetails } from '../../../../store/api';
import { ProductCopyStockDropdown } from '../../../../shared/product/ProductCopyStock';
import { useTranslation } from 'react-i18next';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../../shared/loanFormat/constants';

export const ExtendedRenewalIndividualRow: FC<ExtendedRenewalIndividual> = props => {
    const { currentYearSubscription, nextYearRenewalLine, previousIssueUsage, currentIssueUsage } = props;
    const { t } = useTranslation();
    return (
        <tr className="individual-row">
            <td></td>
            <td className="usage-limit-cell">
                <div>
                    <b>{t('shop.subscriptions.individual')}</b>
                </div>
            </td>
            <td className="current-year-subscriptions">
                {(() => {
                    switch (currentYearSubscription.type) {
                        case ExtendedManageLineUsageLimit.Value:
                            return (
                                <ExtendedUsageLimit
                                    label={currentYearSubscription.label}
                                    stockDetails={currentYearSubscription.stockDetails}
                                />
                            );
                        case ExtendedManageLineUsageLimit.None:
                            return <small>{currentYearSubscription.label}</small>;
                        default:
                            return <></>;
                    }
                })()}
            </td>
            <td className="previous-issue-usage">
                {previousIssueUsage ? <IssueUsageStatisticsCell {...previousIssueUsage} /> : null}
            </td>
            <td className="current-issue-usage">
                {currentIssueUsage ? <IssueUsageStatisticsCell {...currentIssueUsage} /> : null}
            </td>
            {(() => {
                switch (nextYearRenewalLine.type) {
                    case RenewalLineView.RenewalLine:
                        return <RenewalLineCell {...nextYearRenewalLine} />;
                    case RenewalLineView.Adapted_Recommended:
                        return <AdaptedRecommendCell {...nextYearRenewalLine} />;
                    case RenewalLineView.Adapted_Custom:
                        return <AdaptedCustomCell {...nextYearRenewalLine} />;
                    case RenewalLineView.Renewed:
                        return (
                            <td colSpan={3} className="renewal-already-ordered">
                                <div>
                                    <b>{nextYearRenewalLine.label}</b>
                                    <div>{t('shop.subscriptions.alreadyOrdered')}</div>
                                </div>
                            </td>
                        );
                    case RenewalLineView.Unavailable:
                        return (
                            <td colSpan={3} className="renewal-unavailable">
                                <div>
                                    <b>{t('shop.subscriptions.unavailable')}</b>
                                </div>
                            </td>
                        );
                    default:
                        return <></>;
                }
            })()}
        </tr>
    );
};

const ExtendedUsageLimit: FC<{ label: string; stockDetails: StockDetails }> = ({ label, stockDetails }) => {
    const ref = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    return (
        <>
            <b ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                {label}
            </b>
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};

const AdaptedRecommendCell = (nextYearRenewalLine: ExtendedAdaptedRecommendedLine) => {
    const { t } = useTranslation();
    return (
        <>
            <td colSpan={2} className="renewal-adapted-recommended">
                <small>{t('shop.subscriptions.noIndividualRecommended')}</small>
                <Button
                    onClick={() =>
                        nextYearRenewalLine.onRenewalLineUpdate({
                            type: 'CUSTOM',
                            lineItems: nextYearRenewalLine.items.map(item => ({
                                usageCondition: {
                                    usageLimit: item.price.usageLimit,
                                    lendingModel: item.price.lendingModel,
                                    termLimit: item.price.termLimit
                                },
                                amount: item.amount
                            }))
                        })
                    }
                    text={t('shop.subscriptions.purchaseIndividualCopy')}
                    theme={ButtonTheme.NEUTRAL}
                    className="gild"
                    size={ButtonSize.MEDIUM}
                    icon="shopping_cart"
                />
            </td>
            <NoTotalCell />
        </>
    );
};

const AdaptedCustomCell = ({ items, total, onRenewalLineUpdate }: ExtendedAdaptedCustomLine) => {
    const { t } = useTranslation();
    return (
        <>
            <td className="renewal-adapted-custom">
                <Button
                    onClick={() =>
                        onRenewalLineUpdate({
                            type: 'RECOMMENDED',
                            lineItems: null
                        })
                    }
                    text={t('shop.subscriptions.dontPurchase')}
                    theme={ButtonTheme.NEUTRAL}
                    className="gild"
                    size={ButtonSize.MEDIUM}
                    icon="close"
                />
            </td>
            <RenewalLineItems
                items={items}
                onAmountChange={lineItems =>
                    onRenewalLineUpdate({
                        type: 'CUSTOM',
                        lineItems
                    })
                }
            />
            <RenewalLineTotal total={total} />
        </>
    );
};
